/**
 * @i18n-channel model
 *
 * The prose in this file is read by the MODEL, not by an operator — over two
 * different transports, which is exactly why the marker says `model` and not
 * `prompt`:
 *
 *   1. `readback.description` and `undo.description` go into the PROMPT.
 *      `formatStudioChatOperationInstructions`
 *      (studio-chat-operation-registry.ts:1561) serializes them into
 *      `chatOperationInstructions`, which `chat-model-adapter.ts:500` and
 *      `gateway-chat-service.ts:80` put on the wire.
 *   2. `preconditions[].description` is NOT in the prompt at all. It reaches
 *      the model through the WebMCP capability tool response —
 *      `studio-capability-gateway.ts:4707` and :4721 — and has no UI sink.
 *
 * Marking this file `prompt` was therefore inaccurate for case 2; `model` is
 * the honest name. Translating either set would change what the model reads.
 *
 * The operator-facing copy for these operations lives in
 * `studio-operation-display-copy.ts`, keyed by operation id, and the render
 * sinks resolve it there.
 *
 * The marker above is a CLAIM, and it is backed by a gate rather than by trust:
 * `__tests__/studio-chat-operation-prompt-invariance.test.ts` byte-compares all
 * 93 operations against a recorded baseline and fails on any non-ASCII in the
 * prompt lines, and `__tests__/i18n-channel-marker.test.ts` asserts that every
 * file carrying this marker is one whose strings actually appear in that
 * baseline. Do not copy this marker to a file without such a gate — on its own
 * it is a silencer.
 */
import type {
  StudioDirectUiStatus,
  StudioJsonSchema,
  StudioModuleId,
  StudioOperationConfirmation,
  StudioOperationPrecondition,
  StudioOperationReadback,
  StudioOperationRisk,
  StudioOperationUndo,
  StudioOperationV2Profile,
  StudioTargetKind,
} from "./studio-operation-v2";

const text: StudioJsonSchema = { type: "string", minLength: 1 };
const integer: StudioJsonSchema = { type: "integer", minimum: 0 };
const record: StudioJsonSchema = { type: "object", additionalProperties: true };
const nonEmptyRecord: StudioJsonSchema = {
  type: "object",
  additionalProperties: true,
  minProperties: 1,
};
const anyValue: StudioJsonSchema = {};

function choice(...values: string[]): StudioJsonSchema {
  return { type: "string", enum: values };
}

function list(items: StudioJsonSchema = text): StudioJsonSchema {
  return { type: "array", items };
}

function input(
  properties: Record<string, StudioJsonSchema> = {},
  required: readonly string[] = [],
): StudioJsonSchema & { type: "object" } {
  return {
    type: "object",
    properties,
    required,
    additionalProperties: false,
  };
}

interface ProfileOptions {
  confirmation?: StudioOperationConfirmation;
  preconditions?: readonly StudioOperationPrecondition[];
  readback?: StudioOperationReadback;
  undo?: StudioOperationUndo;
  developerMode?: boolean;
  examples?: readonly string[];
}

function profile(
  moduleId: StudioModuleId,
  targetKinds: readonly StudioTargetKind[],
  inputSchema: StudioJsonSchema & { type: "object" },
  risk: StudioOperationRisk,
  directUi: StudioDirectUiStatus,
  options: ProfileOptions = {},
): StudioOperationV2Profile {
  return {
    moduleId,
    targetKinds,
    inputSchema,
    risk,
    directUi,
    ...options,
  };
}

const EMPTY = input();
const SEMANTIC_SYNC = input(
  {
    changeLabel: text,
    pageId: text,
    appTitle: text,
    primaryColor: text,
    documentNodeId: text,
    guideText: text,
  },
  [
    "changeLabel",
    "pageId",
    "appTitle",
    "primaryColor",
    "documentNodeId",
    "guideText",
  ],
);
const ONTOLOGY_KNOWLEDGE_SEARCH = input(
  {
    query: { type: "string", minLength: 1 },
    maxResults: { type: "integer", minimum: 1, maximum: 32 },
    subjectId: text,
    target: choice("cogo-web", "cogo-flutter", "cogo-app-host"),
  },
  ["query"],
);
const SOURCE_SUFFICIENCY_READ = input({ subjectId: text });
const SOURCE_SUFFICIENCY_ANSWER = input(
  {
    subjectId: text,
    clarificationRequestId: text,
    decision: text,
  },
  ["clarificationRequestId", "decision"],
);
const PAGE_META = input(
  {
    pageId: text,
    title: text,
    description: text,
    route: text,
    layout: text,
  },
  ["pageId"],
);
const PAGE_BLOCKS = input(
  {
    pageId: text,
    operation: choice("replace", "append", "remove", "move"),
    blockNames: list(text),
    toIndex: integer,
  },
  ["pageId", "operation"],
);
const PAGE_DELETE = input({ pageId: text }, ["pageId"]);
const ELEMENT_ADD = input(
  {
    blockName: text,
    component_type: text,
    parentId: text,
    label: text,
    text,
    content: text,
    src: text,
    properties: record,
  },
  ["blockName", "component_type"],
);
const ELEMENT_UPDATE = input(
  { blockName: text, elementId: text, changes: nonEmptyRecord },
  ["blockName", "elementId", "changes"],
);
const ELEMENT_REMOVE = input({ blockName: text, elementId: text }, [
  "blockName",
  "elementId",
]);
const FLOW_CREATE = input(
  {
    name: text,
    trigger: choice("event", "lifecycle", "init", "manual"),
    preserveSurface: { type: "boolean" },
  },
  ["name"],
);
const FLOW_RENAME = input(
  { flowId: text, blockName: text, name: text, newName: text },
  ["flowId", "newName"],
);
const FLOW_EDIT = input({ flowId: text, op: text, args: list() }, [
  "flowId",
  "op",
  "args",
]);
const FLOW_BINDING = input(
  {
    blockName: text,
    eventKey: text,
    flow: text,
    element: record,
  },
  ["blockName", "eventKey", "flow", "element"],
);
const EVENT_BINDING_REMOVE = input(
  { blockName: text, eventKey: text, element: record },
  ["blockName", "eventKey"],
);
const FLOW_DELETE = input({ flowId: text, blockName: text, name: text }, [
  "flowId",
]);
const VALIDATION = input({ blockName: text });
const VARIABLE = input(
  {
    blockName: text,
    scope: choice("state", "computed", "context"),
    name: text,
    type: choice("string", "number", "boolean", "array", "object", "any"),
    default: anyValue,
  },
  ["blockName", "scope", "name", "type"],
);
const VARIABLE_UPDATE = input(
  {
    blockName: text,
    scope: choice("state", "computed", "context"),
    name: text,
    type: choice("string", "number", "boolean", "array", "object", "any"),
    default: anyValue,
  },
  ["blockName", "scope", "name"],
);
VARIABLE_UPDATE.anyOf = [{ required: ["type"] }, { required: ["default"] }];
const VARIABLE_REMOVE = input(
  {
    blockName: text,
    scope: choice("state", "computed", "context"),
    name: text,
  },
  ["blockName", "scope", "name"],
);
const DATASOURCE = input(
  {
    blockName: text,
    name: text,
    type: choice("rest", "graphql", "supabase", "static", "computed"),
    status: choice("ready", "needs-configuration"),
    method: text,
    url: text,
    source: text,
    config: record,
  },
  ["blockName", "name", "type"],
);
const DATASOURCE_UPDATE = input(
  {
    blockName: text,
    name: text,
    type: choice("rest", "graphql", "supabase", "static", "computed"),
    status: choice("ready", "needs-configuration"),
    method: text,
    url: text,
    source: text,
    config: record,
  },
  ["blockName", "name"],
);
DATASOURCE_UPDATE.anyOf = [
  { required: ["type"] },
  { required: ["status"] },
  { required: ["method"] },
  { required: ["url"] },
  { required: ["source"] },
  { required: ["config"] },
];
const DATA_BINDING = input(
  {
    blockName: text,
    elementId: text,
    sourceName: text,
    bindingExpression: text,
    mode: choice("value", "collection", "options"),
  },
  ["blockName", "elementId", "sourceName", "bindingExpression", "mode"],
);
const DATA_REFRESH = input({ name: text }, ["name"]);
const DATASOURCE_REMOVE = input({ blockName: text, name: text }, [
  "blockName",
  "name",
]);
const THEME_PRESET = input(
  { presetId: choice("modern", "warm", "professional", "vibrant", "minimal") },
  ["presetId"],
);
const THEME_COLOR = input({ key: text, value: text }, ["key", "value"]);
const THEME_FONT = input({ fontFamily: text }, ["fontFamily"]);
const THEME_RADIUS = input({ radius: choice("none", "sm", "md", "lg") }, [
  "radius",
]);
const PROJECT_CREATE = input({ name: text, templateId: text }, ["name"]);
const PROJECT_OPEN = input({ projectId: text }, ["projectId"]);
const PROJECT_RENAME = input({ name: text }, ["name"]);
const PROJECT_META = input({ description: text, icon: text });
PROJECT_META.anyOf = [{ required: ["description"] }, { required: ["icon"] }];
const PROJECT_DELETE = input({ projectId: text }, ["projectId"]);
const PROJECT_DUPLICATE = input({ newName: text });
const APPLICATION_GENERATE = input({ appSpec: record }, ["appSpec"]);
const APPLICATION_CREATE_FROM_BRIEF = input(
  {
    workspaceId: text,
    title: text,
    sourceType: choice(
      "assistant_prompt",
      "github_pr",
      "github_repo",
      "github_commit",
      "ide_file",
      "url_import",
      "manual",
      "template_remix",
    ),
    inputType: choice("prompt", "markdown", "html", "react", "cogo_json"),
    input: text,
    target: choice("landing_page", "dashboard", "admin", "creator_page", "app"),
    createdBy: text,
  },
  ["workspaceId", "title", "sourceType", "inputType", "input", "target"],
);
const APPLICATION_PATCH = input(
  {
    patches: list(record),
  },
  ["patches"],
);
const APPLICATION_BUNDLE_EXPORT = input(
  {
    bundleId: text,
    format: choice("cogo_json", "react", "html", "figma", "deploy_package"),
  },
  ["bundleId", "format"],
);
const OPENPENCIL_SOURCE_IMPORT = input(
  {
    sourceBundle: record,
    openInSourceSurface: { type: "boolean" },
  },
  ["sourceBundle"],
);
const FIGMA_TOKEN_VALIDATE = input(
  {
    figmaAccessToken: text,
    workspaceId: text,
  },
  ["figmaAccessToken"],
);
const FIGMA_FRAME_IMPORT = input(
  {
    workspaceId: text,
    figmaFileId: text,
    figmaNodeId: text,
    createPreview: { type: "boolean" },
    createdBy: text,
    figmaAccessToken: text,
    figmaTokenRef: text,
    figmaUrl: text,
    audience: text,
    tags: list(text),
    // Mirrors the hardened cogo.studio.import_figma_frame tool schema:
    // template intent must be user-stated, never derived from the source.
    templateSelection: {
      type: "object",
      required: ["userIntent", "scope"],
      properties: {
        userIntent: text,
        scope: {
          type: "string",
          enum: ["project", "template", "designSystem", "globalLibrary"],
        },
        expectedViewportClass: {
          type: "string",
          enum: ["mobile", "tablet", "desktop", "unknown"],
        },
        expectedRegions: list(text),
        expectedComponentKinds: list(text),
        expectedInteractionAffordances: list(text),
      },
    },
  },
  [
    "workspaceId",
    "figmaFileId",
    "figmaNodeId",
    "createPreview",
    "templateSelection",
  ],
);
const GITHUB_PREVIEW_IMPORT = input(
  {
    workspaceId: text,
    repo: text,
    prNumber: integer,
    createdBy: text,
  },
  ["workspaceId", "repo", "prNumber"],
);
const FIGMA_SOURCE = input({ workspaceId: text, sourceRevision: text });
const FIGMA_EDIT = input(
  {
    actor: text,
    reason: text,
    operations: list(record),
  },
  ["actor", "reason", "operations"],
);
const FIGMA_DESIGN_MUTATION_DRAFT = input(
  {
    mutationId: text,
    reason: text,
    operations: list(record),
    evidenceRefs: list(text),
  },
  ["reason", "operations"],
);
const FIGMA_DESIGN_MUTATION = input({ proposal: record }, ["proposal"]);
const FIGMA_MAPPING = input({ actor: text, reason: text }, ["actor", "reason"]);
const FIGMA_TOKENS = input(
  { status: choice("accepted", "rejected"), actor: text },
  ["status", "actor"],
);
const FIGMA_VISUAL = input({ surfaceIds: list(text) });
const FIGMA_ASSEMBLY = input({ evidenceRefs: list(text) });
const FIGMA_CANDIDATE = input(
  { workspaceId: text, title: text, input: text, target: { const: "app" } },
  ["workspaceId", "title", "input", "target"],
);
const REPORT_EDIT = input(
  {
    replacementText: text,
    target: choice("pdf", "docx", "pptx", "xlsx"),
  },
  ["replacementText", "target"],
);
const PAGE_EXPORT = input(
  {
    pageId: text,
    target: choice("docx", "hwpx", "pptx", "pdf", "xlsx"),
  },
  ["pageId", "target"],
);
const DOCUMENT_PAGE_CREATE = input(
  { title: text, kind: choice("page", "slide", "sheet") },
  ["title", "kind"],
);
const DOCUMENT_NODE_UPDATE = input(
  { nodeId: text, text, title: text, attributes: record },
  ["nodeId"],
);
const DOCUMENT_PAGE = input({ pageId: text, target: text }, ["pageId"]);
const DOCUMENT_WORDGARD_PAGE = input(
  { pageId: text, target: choice("docx", "hwpx") },
  ["pageId", "target"],
);
const DOCUMENT_PAGE_MOVE = input({ pageId: text, toIndex: integer }, [
  "pageId",
  "toIndex",
]);
const DOCUMENT_ANCHOR = input(
  {
    documentNodeId: text,
    target: choice("docx", "hwpx", "pptx", "pdf", "xlsx"),
  },
  ["documentNodeId"],
);
const DOCUMENT_PROPOSE = input(
  {
    documentNodeId: text,
    documentNodeKind: text,
    target: choice("docx", "hwpx", "pptx", "pdf", "xlsx"),
    body: text,
    operationPayload: record,
  },
  ["documentNodeId", "documentNodeKind", "target", "body"],
);
const PROPOSAL = input({ proposalId: text }, ["proposalId"]);
const DOCUMENT_BLOCKER = input({ proposalId: text, target: text });
const DOCUMENT_TARGET = input(
  { target: choice("docx", "hwpx", "pptx", "pdf", "xlsx") },
  ["target"],
);
const RELEASE_PREVIEW_SHARE = input(
  {
    previewUrl: text,
    visibility: choice("public", "unlisted"),
    createdBy: text,
  },
  ["previewUrl", "visibility"],
);
const RELEASE_IDENTIFIED = input({ projectId: text, releaseId: text }, [
  "releaseId",
]);
const RELEASE_EXACT_EFFECT_CONFIRMATION = input(
  {
    confirmed: { type: "boolean", const: true },
    candidateId: text,
    outputHash: text,
    promotionHash: text,
  },
  ["confirmed", "candidateId", "outputHash"],
);
const RELEASE_DEPLOY = input(
  {
    projectId: text,
    releaseId: text,
    environment: choice("staging", "production"),
    adapterId: text,
    confirmation: RELEASE_EXACT_EFFECT_CONFIRMATION,
  },
  ["releaseId", "environment", "confirmation"],
);
const RELEASE_CONTENT_REF = input({ ref: text, contentHash: text }, [
  "ref",
  "contentHash",
]);
const RELEASE_RECEIPT_CONFIRMATION = input(
  {
    confirmed: { type: "boolean", const: true },
    candidateId: text,
    outputHash: text,
    targetDeploymentReceiptRef: RELEASE_CONTENT_REF,
  },
  ["confirmed", "candidateId", "outputHash", "targetDeploymentReceiptRef"],
);
const RELEASE_ROLLBACK = input(
  {
    projectId: text,
    releaseId: text,
    targetDeploymentReceiptRef: RELEASE_CONTENT_REF,
    confirmation: RELEASE_RECEIPT_CONFIRMATION,
  },
  ["releaseId", "targetDeploymentReceiptRef", "confirmation"],
);

const ARTIFACT_READBACK: StudioOperationReadback = {
  mode: "artifact-proof",
  description:
    "Return a checksum or a check of the written file before claiming the artifact is ready.",
};

const CORE_SURFACE_PERSISTENCE_READBACK: StudioOperationReadback = {
  mode: "surface-state",
  description:
    "Verify the owning Studio working copy after the mutation. For a server-backed project, flush both save lanes and compare an authoritative server GET; local projects remain working-copy-only.",
};

const LOCAL_WORKING_COPY_READBACK: StudioOperationReadback = {
  mode: "surface-state",
  description:
    "Verify the owning Studio local working copy after the mutation. This does not prove a server save acknowledgement or a server GET.",
};

const ELEMENT_TARGET_READBACK: StudioOperationReadback = {
  mode: "surface-state",
  description:
    "Verify the exact blockName and elementId postcondition returned by the element tool. This target-scoped working-copy check does not prove a server save acknowledgement or a server GET.",
};

const VARIABLE_TARGET_READBACK: StudioOperationReadback = {
  mode: "surface-state",
  description:
    "Verify the exact blockName, scope, and variable name postcondition returned by the variable tool. This target-scoped working-copy check does not prove a server save acknowledgement or a server GET.",
};

const DATASOURCE_TARGET_READBACK: StudioOperationReadback = {
  mode: "surface-state",
  description:
    "Verify the exact blockName and datasource name postcondition returned by the datasource tool. This target-scoped working-copy check does not prove a server save acknowledgement or a server GET.",
};

const LOCAL_PROJECT_STATE_READBACK: StudioOperationReadback = {
  mode: "surface-state",
  description:
    "Verify the resulting Studio project list or active-project state. This local check does not prove a server save acknowledgement or a reconnect GET.",
};

const PROJECT_OPEN_TARGET_READBACK: StudioOperationReadback = {
  mode: "surface-state",
  description:
    "Verify that requestedProjectId and currentProjectId match, currentProject is loaded, and projectLoadError is absent. This navigation check does not prove a new server write.",
};

const LOCAL_APP_DESIGN_READBACK: StudioOperationReadback = {
  mode: "surface-state",
  description:
    "Verify the generated application's design tokens and preview state. This working-copy check does not prove a server save acknowledgement or a reconnect GET.",
};

const APPLICATION_GENERATION_READBACK: StudioOperationReadback = {
  mode: "surface-state",
  description:
    "Verify the generated app in the Studio working copy and runtime preview. Do not claim server persistence without a save acknowledgement and an authoritative read from the server.",
};

const EXTERNAL_IMPORT_READBACK: StudioOperationReadback = {
  mode: "surface-state",
  description:
    "Return the bounded tool result and verify the resulting Studio surface. This does not prove external availability, server persistence, or a checked live preview.",
};

const FIGMA_TOKEN_RESULT_READBACK: StudioOperationReadback = {
  mode: "tool-result",
  description:
    "Return only the bounded token-validation control result. Never echo the token, and do not infer durable credential storage without confirming it on the server.",
};

const TRACEABLE_BUNDLE_READBACK: StudioOperationReadback = {
  mode: "artifact-proof",
  description:
    "Return the traceable bundle artifact reference. Treat downloadability or external availability as unverified until an independent check of the file succeeds.",
};

const SOURCE_WORKSPACE_UNDO: StudioOperationUndo = {
  mode: "latest",
  description:
    "Use the import tool's retained inverse snapshot to restore the previous source workspace.",
};

const NO_AUTOMATED_UNDO: StudioOperationUndo = {
  mode: "none",
  description:
    "This registered tool has no exact inverse command. Apply a separate explicit change if the result must be replaced.",
};

const RELEASE_ARTIFACT_READBACK: StudioOperationReadback = {
  mode: "artifact-proof",
  description:
    "Return the canonical release projection and retained evidence refs. A deployment is not complete until its receipt and live-server hashes match after cogo.studio.refreshPublicationReadback.",
  toolName: "cogo.studio.refreshPublicationReadback",
};

const RELEASE_PREVIEW_READBACK: StudioOperationReadback = {
  mode: "artifact-proof",
  description:
    "Retain the exact preview URL and share evidence returned by the tool. A recorded share is not live-server proof and remains ineligible for a live claim without an independent check.",
  toolName: "cogo.studio.share_cogo_preview",
};

const RELEASE_ROLLBACK_COMPENSATION: StudioOperationUndo = {
  mode: "compensating",
  description:
    "External deployment state cannot be undone locally. Compensate only with cogo.studio.rollbackPublicationReceipt against an exact server-authorized same-environment receipt and a new typed confirmation.",
};

const RELEASE_ROLLBACK_RECOMPENSATION: StudioOperationUndo = {
  mode: "compensating",
  description:
    "A completed rollback has no local inverse. A later change must be another explicitly confirmed receipt-level rollback to an exact server-authorized target.",
};

export const STUDIO_OPERATION_V2_PROFILES = {
  "workspace.semantic-sync": profile(
    "application-preview",
    ["app"],
    SEMANTIC_SYNC,
    "external",
    "partial",
    {
      confirmation: "typed",
      readback: {
        mode: "artifact-proof",
        description:
          "Retain exact App, Design, and Guide checks plus the locked publication-candidate receipt. This does not claim production deployment.",
      },
      examples: [
        "Roll out the approved brand name, primary color, and guide copy everywhere, then prepare one publication candidate.",
      ],
    },
  ),
  "page.details.review": profile(
    "page-editor",
    ["page"],
    EMPTY,
    "read",
    "available",
  ),
  "page.details.rename": profile(
    "page-editor",
    ["page"],
    PAGE_META,
    "reversible",
    "available",
  ),
  "page.details.route": profile(
    "page-editor",
    ["page"],
    PAGE_META,
    "reversible",
    "available",
  ),
  "page.details.blocks": profile(
    "page-editor",
    ["page"],
    PAGE_BLOCKS,
    "reversible",
    "available",
  ),
  "page.details.layout": profile(
    "page-editor",
    ["page"],
    PAGE_META,
    "reversible",
    "available",
  ),
  "page.details.delete": profile(
    "page-editor",
    ["page"],
    PAGE_DELETE,
    "destructive",
    "partial",
    { confirmation: "typed", readback: CORE_SURFACE_PERSISTENCE_READBACK },
  ),
  "element.add": profile(
    "page-editor",
    ["page"],
    ELEMENT_ADD,
    "reversible",
    "partial",
    { readback: ELEMENT_TARGET_READBACK },
  ),
  "element.update": profile(
    "page-editor",
    ["element"],
    ELEMENT_UPDATE,
    "reversible",
    "partial",
    { readback: ELEMENT_TARGET_READBACK },
  ),
  "element.remove": profile(
    "page-editor",
    ["element"],
    ELEMENT_REMOVE,
    "reversible",
    "partial",
    { readback: ELEMENT_TARGET_READBACK },
  ),
  "behavior.surface.review": profile(
    "actionflow",
    ["app", "flow"],
    EMPTY,
    "read",
    "available",
  ),
  "behavior.flow.review": profile(
    "actionflow",
    ["flow"],
    EMPTY,
    "read",
    "available",
  ),
  "flow.create": profile(
    "actionflow",
    ["app"],
    FLOW_CREATE,
    "reversible",
    "partial",
    { readback: LOCAL_WORKING_COPY_READBACK },
  ),
  "flow.rename": profile(
    "actionflow",
    ["flow"],
    FLOW_RENAME,
    "reversible",
    "partial",
    { readback: LOCAL_WORKING_COPY_READBACK },
  ),
  "behavior.flow.add-step": profile(
    "actionflow",
    ["flow"],
    FLOW_EDIT,
    "reversible",
    "available",
  ),
  "behavior.flow.connect-selected": profile(
    "actionflow",
    ["element", "flow"],
    FLOW_BINDING,
    "reversible",
    "available",
  ),
  "event-binding.remove": profile(
    "actionflow",
    ["element", "flow"],
    EVENT_BINDING_REMOVE,
    "reversible",
    "partial",
    { readback: LOCAL_WORKING_COPY_READBACK },
  ),
  "behavior.flow.test": profile(
    "actionflow",
    ["flow", "app"],
    VALIDATION,
    "read",
    "available",
    {
      readback: {
        mode: "validation-proof",
        description: "Return ActionFlow validation and runtime proof.",
      },
    },
  ),
  "behavior.flow.delete": profile(
    "actionflow",
    ["flow"],
    FLOW_DELETE,
    "destructive",
    "partial",
    { confirmation: "typed", readback: CORE_SURFACE_PERSISTENCE_READBACK },
  ),
  "data.surface.review": profile(
    "data-source",
    ["app", "datasource"],
    EMPTY,
    "read",
    "partial",
  ),
  "evidence.checks.review": profile(
    "application-preview",
    ["app"],
    EMPTY,
    "read",
    "available",
    {
      examples: [
        "Review the current Checks evidence and explain the blockers.",
      ],
    },
  ),
  "evidence.candidate.read": profile(
    "application-preview",
    ["app"],
    EMPTY,
    "read",
    // The dedicated AskCogoSurfaceButton on the candidate-verification panel
    // was removed by the Chat entry-point consolidation (#1874); the fresh
    // verification readback is reachable from chat only.
    "chat-only",
    {
      readback: {
        mode: "tool-result",
        description:
          "Return the fresh candidate verification tool result derived from the authenticated durable journal. Only overall=verified with durable-journal-verified authority is a pass; pending, checking, ready, or unavailable is not.",
      },
      examples: [
        "Read the current candidate verification state and explain any pending or blocked evidence roles.",
      ],
    },
  ),
  "evidence.sufficiency.read": profile(
    "application-preview",
    ["app"],
    SOURCE_SUFFICIENCY_READ,
    "read",
    "available",
    {
      examples: [
        "Read the current source sufficiency assessment and explain the next decisions.",
      ],
    },
  ),
  "evidence.sufficiency.answer": profile(
    "application-preview",
    ["app"],
    SOURCE_SUFFICIENCY_ANSWER,
    "external",
    "available",
    {
      confirmation: "user",
      readback: {
        mode: "artifact-proof",
        description:
          "Return the BFF-created immutable assessment revision and refreshed deterministic Situation; this does not modify App, Design, Guide, or publication state.",
      },
      undo: {
        mode: "none",
        description:
          "Answers are retained in the immutable source-assessment ledger. Correct one only through a new explicit source clarification decision.",
      },
      examples: [
        "Answer the current source clarification with one BFF-provided option, then refresh the assessment.",
      ],
    },
  ),
  "evidence.knowledge.search": profile(
    "application-preview",
    ["app"],
    ONTOLOGY_KNOWLEDGE_SEARCH,
    "read",
    "available",
    {
      examples: [
        "Search the current approved application knowledge for how approval works.",
      ],
    },
  ),
  "variable.add": profile(
    "data-source",
    ["app"],
    VARIABLE,
    "reversible",
    "partial",
    { readback: VARIABLE_TARGET_READBACK },
  ),
  "variable.update": profile(
    "data-source",
    ["app"],
    VARIABLE_UPDATE,
    "reversible",
    "partial",
    { readback: VARIABLE_TARGET_READBACK },
  ),
  "variable.remove": profile(
    "data-source",
    ["app"],
    VARIABLE_REMOVE,
    "destructive",
    "partial",
    { confirmation: "typed", readback: VARIABLE_TARGET_READBACK },
  ),
  "data.source.add": profile(
    "data-source",
    ["app", "datasource"],
    DATASOURCE,
    "reversible",
    "partial",
    { readback: DATASOURCE_TARGET_READBACK },
  ),
  "data.source.scaffold-from-chat": profile(
    "data-source",
    ["app", "datasource"],
    DATASOURCE,
    "reversible",
    "chat-only",
    { readback: DATASOURCE_TARGET_READBACK },
  ),
  "datasource.update": profile(
    "data-source",
    ["datasource"],
    DATASOURCE_UPDATE,
    "reversible",
    "partial",
    { readback: DATASOURCE_TARGET_READBACK },
  ),
  "data.binding.connect": profile(
    "data-source",
    ["data-binding", "element", "datasource"],
    DATA_BINDING,
    "reversible",
    "partial",
  ),
  "data.flow.debug": profile(
    "data-source",
    ["datasource"],
    DATA_REFRESH,
    "read",
    "partial",
  ),
  "data.source.remove": profile(
    "data-source",
    ["datasource"],
    DATASOURCE_REMOVE,
    "destructive",
    "partial",
    { confirmation: "typed", readback: CORE_SURFACE_PERSISTENCE_READBACK },
  ),
  "application.generate": profile(
    "application-preview",
    ["app"],
    APPLICATION_GENERATE,
    "external",
    "partial",
    {
      confirmation: "user",
      readback: APPLICATION_GENERATION_READBACK,
      undo: NO_AUTOMATED_UNDO,
    },
  ),
  "application.create-from-brief": profile(
    "application-preview",
    ["app"],
    APPLICATION_CREATE_FROM_BRIEF,
    "external",
    "partial",
    {
      confirmation: "user",
      readback: EXTERNAL_IMPORT_READBACK,
      undo: NO_AUTOMATED_UNDO,
    },
  ),
  "application.patch": profile(
    "cogo-json",
    ["app"],
    APPLICATION_PATCH,
    "reversible",
    "chat-only",
    { readback: LOCAL_WORKING_COPY_READBACK },
  ),
  "application.bundle.export": profile(
    "cogo-json",
    ["app"],
    APPLICATION_BUNDLE_EXPORT,
    "external",
    "developer-only",
    {
      confirmation: "user",
      readback: TRACEABLE_BUNDLE_READBACK,
      undo: NO_AUTOMATED_UNDO,
      developerMode: true,
    },
  ),
  "figma.source.import": profile(
    "figma",
    ["app", "figma-source"],
    OPENPENCIL_SOURCE_IMPORT,
    "reversible",
    "partial",
    {
      confirmation: "user",
      readback: EXTERNAL_IMPORT_READBACK,
      undo: SOURCE_WORKSPACE_UNDO,
    },
  ),
  "figma.token.validate": profile(
    "figma",
    ["app", "figma-source"],
    FIGMA_TOKEN_VALIDATE,
    "external",
    "partial",
    {
      confirmation: "user",
      readback: FIGMA_TOKEN_RESULT_READBACK,
      undo: NO_AUTOMATED_UNDO,
    },
  ),
  "figma.frame.import": profile(
    "figma",
    ["app"],
    FIGMA_FRAME_IMPORT,
    "external",
    "partial",
    {
      confirmation: "user",
      readback: EXTERNAL_IMPORT_READBACK,
      undo: NO_AUTOMATED_UNDO,
    },
  ),
  "github.preview.import": profile(
    "application-preview",
    ["app"],
    GITHUB_PREVIEW_IMPORT,
    "external",
    "partial",
    {
      confirmation: "user",
      readback: EXTERNAL_IMPORT_READBACK,
      undo: NO_AUTOMATED_UNDO,
    },
  ),
  "source.figma.inspect": profile(
    "figma",
    ["figma-source"],
    FIGMA_SOURCE,
    "read",
    "partial",
  ),
  "source.figma.export_original": profile(
    "figma",
    ["figma-source"],
    FIGMA_SOURCE,
    "read",
    "partial",
    { readback: ARTIFACT_READBACK },
  ),
  "source.figma.edit": profile(
    "figma",
    ["figma-source"],
    FIGMA_EDIT,
    "reversible",
    "partial",
  ),
  "source.figma.apply_design_mutation": profile(
    "figma",
    ["figma-source", "element"],
    FIGMA_DESIGN_MUTATION,
    "reversible",
    "partial",
  ),
  "source.figma.propose_design_mutation": profile(
    "figma",
    ["figma-source", "element"],
    FIGMA_DESIGN_MUTATION_DRAFT,
    "read",
    "partial",
  ),
  "source.figma.apply_mapping": profile(
    "figma",
    ["figma-source"],
    FIGMA_MAPPING,
    "reversible",
    "partial",
  ),
  "source.figma.decide_tokens": profile(
    "figma",
    ["figma-source"],
    FIGMA_TOKENS,
    "reversible",
    "partial",
  ),
  "source.figma.visual_review": profile(
    "figma",
    ["figma-source"],
    FIGMA_VISUAL,
    "external",
    "partial",
  ),
  "source.figma.assemble": profile(
    "figma",
    ["figma-source"],
    FIGMA_ASSEMBLY,
    "reversible",
    "partial",
    { developerMode: true },
  ),
  "source.figma.runtime_proof": profile(
    "figma",
    ["figma-source", "app"],
    EMPTY,
    "external",
    "developer-only",
    { developerMode: true },
  ),
  "source.figma.create_candidate": profile(
    "figma",
    ["figma-source", "app"],
    FIGMA_CANDIDATE,
    "reversible",
    "developer-only",
    { developerMode: true },
  ),
  "report.workspace.review": profile(
    "report",
    ["report"],
    EMPTY,
    "read",
    "partial",
  ),
  "report.document-page.review": profile(
    "report",
    ["report-section", "document-page"],
    EMPTY,
    "read",
    "partial",
  ),
  "report.document-page.edit": profile(
    "report",
    ["report-section", "document-page"],
    REPORT_EDIT,
    "reversible",
    "partial",
  ),
  "report.document-page.validate": profile(
    "report",
    ["report", "document-page"],
    VALIDATION,
    "read",
    "developer-only",
    {
      developerMode: true,
      readback: {
        mode: "validation-proof",
        description: "Return report and document-page validation proof.",
      },
    },
  ),
  "report.document-page.export": profile(
    "report",
    ["document-page", "export-target"],
    PAGE_EXPORT,
    "external",
    "partial",
    { readback: ARTIFACT_READBACK },
  ),
  "report.export-target.validate": profile(
    "report",
    ["export-target"],
    VALIDATION,
    "read",
    "developer-only",
    {
      developerMode: true,
      readback: {
        mode: "validation-proof",
        description: "Return native export target validation proof.",
      },
    },
  ),
  "document.workspace.inspect": profile(
    "document",
    ["document"],
    EMPTY,
    "read",
    "partial",
  ),
  "document.page.inspect": profile(
    "document",
    ["document-page"],
    DOCUMENT_PAGE,
    "read",
    "partial",
  ),
  "document.page.create": profile(
    "document",
    ["document"],
    DOCUMENT_PAGE_CREATE,
    "reversible",
    "partial",
  ),
  "document.page.update_node": profile(
    "document",
    ["document-node"],
    DOCUMENT_NODE_UPDATE,
    "reversible",
    "partial",
  ),
  "document.page.wordgard": profile(
    "document",
    ["document-page"],
    DOCUMENT_WORDGARD_PAGE,
    "read",
    "partial",
  ),
  "document.page.move": profile(
    "document",
    ["document-page"],
    DOCUMENT_PAGE_MOVE,
    "reversible",
    "partial",
  ),
  "document.page.delete": profile(
    "document",
    ["document-page"],
    DOCUMENT_PAGE,
    "destructive",
    "partial",
    { confirmation: "typed", readback: CORE_SURFACE_PERSISTENCE_READBACK },
  ),
  "document.page.export": profile(
    "document",
    ["document-page", "export-target"],
    PAGE_EXPORT,
    "external",
    "partial",
    { readback: ARTIFACT_READBACK },
  ),
  "document.page.select_anchor": profile(
    "document",
    ["document-node", "export-target"],
    DOCUMENT_ANCHOR,
    "read",
    "partial",
  ),
  "document.page.propose_edit": profile(
    "document",
    ["document-node", "document-proposal"],
    DOCUMENT_PROPOSE,
    "reversible",
    "partial",
  ),
  "document.proposal.apply": profile(
    "document",
    ["document-proposal"],
    PROPOSAL,
    "reversible",
    "partial",
  ),
  "document.proposal.list": profile(
    "document",
    ["document", "document-proposal"],
    EMPTY,
    "read",
    "partial",
  ),
  "document.proposal.reject": profile(
    "document",
    ["document-proposal"],
    PROPOSAL,
    "reversible",
    "partial",
  ),
  "document.blocker.explain": profile(
    "document",
    ["document-proposal", "export-target"],
    DOCUMENT_BLOCKER,
    "read",
    "partial",
  ),
  "document.target.export": profile(
    "document",
    ["document", "export-target"],
    DOCUMENT_TARGET,
    "external",
    "partial",
    { readback: ARTIFACT_READBACK },
  ),
  "document.target.readback": profile(
    "document",
    ["export-target"],
    DOCUMENT_TARGET,
    "read",
    "partial",
    { readback: ARTIFACT_READBACK },
  ),
  "theme.preset.set": profile(
    "page-editor",
    ["app"],
    THEME_PRESET,
    "reversible",
    "chat-only",
    {
      confirmation: "user",
      readback: LOCAL_APP_DESIGN_READBACK,
    },
  ),
  "theme.color.update": profile(
    "page-editor",
    ["app"],
    THEME_COLOR,
    "reversible",
    "chat-only",
    {
      confirmation: "user",
      readback: LOCAL_APP_DESIGN_READBACK,
    },
  ),
  "theme.font.set": profile(
    "page-editor",
    ["app"],
    THEME_FONT,
    "reversible",
    "chat-only",
    {
      confirmation: "user",
      readback: LOCAL_APP_DESIGN_READBACK,
    },
  ),
  "theme.radius.set": profile(
    "page-editor",
    ["app"],
    THEME_RADIUS,
    "reversible",
    "chat-only",
    {
      confirmation: "user",
      readback: LOCAL_APP_DESIGN_READBACK,
    },
  ),
  "theme.reset": profile(
    "page-editor",
    ["app"],
    EMPTY,
    "reversible",
    "chat-only",
    {
      confirmation: "user",
      readback: LOCAL_APP_DESIGN_READBACK,
    },
  ),
  "project.create": profile(
    "application-preview",
    ["app"],
    PROJECT_CREATE,
    "external",
    "partial",
    {
      confirmation: "user",
      readback: LOCAL_PROJECT_STATE_READBACK,
      undo: NO_AUTOMATED_UNDO,
    },
  ),
  "project.open": profile(
    "application-preview",
    ["app"],
    PROJECT_OPEN,
    "read",
    "partial",
    { readback: PROJECT_OPEN_TARGET_READBACK },
  ),
  "project.close": profile(
    "application-preview",
    ["app"],
    EMPTY,
    "read",
    "partial",
    { readback: LOCAL_PROJECT_STATE_READBACK },
  ),
  "project.rename": profile(
    "application-preview",
    ["app"],
    PROJECT_RENAME,
    "external",
    "partial",
    {
      confirmation: "user",
      readback: LOCAL_PROJECT_STATE_READBACK,
      undo: NO_AUTOMATED_UNDO,
    },
  ),
  "project.metadata.update": profile(
    "application-preview",
    ["app"],
    PROJECT_META,
    "external",
    "partial",
    {
      confirmation: "user",
      readback: LOCAL_PROJECT_STATE_READBACK,
      undo: NO_AUTOMATED_UNDO,
    },
  ),
  "project.delete": profile(
    "application-preview",
    ["app"],
    PROJECT_DELETE,
    "destructive",
    "partial",
    {
      confirmation: "typed",
      readback: LOCAL_PROJECT_STATE_READBACK,
      undo: NO_AUTOMATED_UNDO,
    },
  ),
  "project.duplicate": profile(
    "application-preview",
    ["app"],
    PROJECT_DUPLICATE,
    "external",
    "partial",
    {
      confirmation: "user",
      readback: LOCAL_PROJECT_STATE_READBACK,
      undo: NO_AUTOMATED_UNDO,
    },
  ),
  "project.publish.confirm": profile(
    "release",
    ["release"],
    EMPTY,
    "external",
    "partial",
    { confirmation: "typed" },
  ),
  "release.preview.share": profile(
    "release",
    ["release"],
    RELEASE_PREVIEW_SHARE,
    "external",
    "partial",
    {
      confirmation: "user",
      preconditions: [
        {
          kind: "source-gates-passed",
          description:
            "The current source release must be admitted before recording a shareable preview.",
        },
      ],
      readback: RELEASE_PREVIEW_READBACK,
      undo: NO_AUTOMATED_UNDO,
      examples: [
        "Share https://preview.example.com as an unlisted Coogo preview.",
      ],
    },
  ),
  "release.verify": profile(
    "release",
    ["release"],
    RELEASE_IDENTIFIED,
    "external",
    "partial",
    {
      confirmation: "user",
      preconditions: [
        {
          kind: "source-gates-passed",
          description:
            "The identified canonical release must currently allow run-verification; verification proof cannot be supplied by chat.",
        },
      ],
      readback: RELEASE_ARTIFACT_READBACK,
      undo: NO_AUTOMATED_UNDO,
      examples: ["Run verification for release rel_123."],
    },
  ),
  "release.deploy": profile(
    "release",
    ["release"],
    RELEASE_DEPLOY,
    "external",
    "partial",
    {
      confirmation: "typed",
      preconditions: [
        {
          kind: "validation-passed",
          description:
            "The canonical release must allow the exact environment deployment and have a matching one-time staging grant or production authorization.",
        },
        {
          kind: "source-gates-passed",
          description:
            "Candidate ID, output hash, adapter, environment, and production promotion hash (when applicable) must match server authority.",
        },
      ],
      readback: RELEASE_ARTIFACT_READBACK,
      undo: RELEASE_ROLLBACK_COMPENSATION,
      examples: [
        "Deploy release rel_123 to staging after confirming its exact candidate and output hash.",
      ],
    },
  ),
  "release.readback": profile(
    "release",
    ["release"],
    RELEASE_IDENTIFIED,
    "external",
    "partial",
    {
      confirmation: "user",
      readback: RELEASE_ARTIFACT_READBACK,
      undo: NO_AUTOMATED_UNDO,
      examples: ["Refresh the live-server check for release rel_123."],
    },
  ),
  "release.rollback": profile(
    "release",
    ["release"],
    RELEASE_ROLLBACK,
    "external",
    "partial",
    {
      confirmation: "typed",
      preconditions: [
        {
          kind: "validation-passed",
          description:
            "The release must be deployed and the target must be an exact server-authorized same-environment deployment receipt.",
        },
        {
          kind: "source-gates-passed",
          description:
            "Candidate ID, target output hash, receipt ref, and receipt content hash must match the canonical rollback target.",
        },
      ],
      readback: RELEASE_ARTIFACT_READBACK,
      undo: RELEASE_ROLLBACK_RECOMPENSATION,
      examples: [
        "Roll release rel_123 back to the exact verified deployment receipt after typed confirmation.",
      ],
    },
  ),
} satisfies Record<string, StudioOperationV2Profile>;

export type StudioOperationId = keyof typeof STUDIO_OPERATION_V2_PROFILES;
